import { isDefined, isNil } from "@clipboard-health/util-ts";
import { get } from "@src/appV2/api";
import { API_TYPES } from "@src/appV2/api/constants";
import { environmentConfig } from "@src/appV2/environment";
import { APP_V2_APP_EVENTS, logEvent } from "@src/appV2/lib/analytics";
import {
  type QueryFunctionContext,
  type QueryKey,
  useInfiniteQuery,
  type UseInfiniteQueryOptions,
  useQueryClient,
} from "@tanstack/react-query";
import { type AxiosError } from "axios";
import { isEmpty } from "lodash";
import { useHistory } from "react-router-dom";
import { type z } from "zod";

import { type GetPlacementsForWorkplaceQuery } from "../../Placements/types/fetchPlacements.schema";
import {
  type HiringFeedPlacement,
  type hiringFeedPlacementSchema,
  type HiringFeedsResponse,
  hiringFeedsResponseSchema,
  type InterviewSettingsInclude,
  type RequirementInclude,
  type TagInclude,
  type WorkerRequirementInclude,
  type WorkplaceInclude,
} from "./hiringFeed.schema";

export function fetchHiringFeedApiUrl(workerId: string): string {
  return `${environmentConfig.REACT_APP_BASE_API_URL}/workers/${workerId}/hiring-feeds`;
}

interface FetchHiringFeedParams {
  workerId: string;
  placementCandidateId: string;
  filter?: GetPlacementsForWorkplaceQuery["filter"];
  workerTypesAvailableForHiring?: string[];
  bookingReorderingCohort?: string;
}

export function usePaginatedHiringFeed(
  params: FetchHiringFeedParams,
  options: UseInfiniteQueryOptions<HiringFeedsResponse, AxiosError> = {}
) {
  const {
    workerId,
    placementCandidateId,
    filter,
    workerTypesAvailableForHiring,
    bookingReorderingCohort,
  } = params;
  const history = useHistory();
  const searchParams = new URLSearchParams(history.location.search);
  const source = searchParams.get("source");

  const queryClient = useQueryClient();

  return useInfiniteQuery({
    queryKey: [fetchHiringFeedApiUrl(workerId), filter, source],
    keepPreviousData: true,
    queryFn: async ({
      pageParam: pageParameter,
    }: QueryFunctionContext<QueryKey, string | undefined>): Promise<HiringFeedsResponse> => {
      const nonEmptyFilter = Object.fromEntries(
        Object.entries(filter ?? {}).filter(([_, value]) => value)
      );

      await queryClient.cancelQueries({ queryKey: [fetchHiringFeedApiUrl(workerId)] });

      const response = await get({
        url: fetchHiringFeedApiUrl(workerId),
        queryParams: {
          page: {
            size: isNil(pageParameter) ? 5 : 15,
            cursor: pageParameter,
          },
          filter: nonEmptyFilter,
        },
        responseSchema: hiringFeedsResponseSchema,
      });
      if (!isEmpty(placementCandidateId)) {
        logEvent(APP_V2_APP_EVENTS.PLACEMENTS_PAGE_VIEWED, {
          placementCandidateId,
          filter: nonEmptyFilter,
          source,
          ...(isDefined(workerTypesAvailableForHiring) &&
            workerTypesAvailableForHiring.length > 0 && {
              workerTypesAvailableForHiring: workerTypesAvailableForHiring.join(","),
            }),
          ...(isDefined(bookingReorderingCohort) && { bookingReorderingCohort }),
        });
      }

      // documents are fetched after the initial database query and aren't supported for filtering / sorting.
      const sortedData = response.data.data.sort((a, b) => {
        if (a.meta.isDocumentRequirementValid && !b.meta.isDocumentRequirementValid) {
          return -1;
        }

        if (!a.meta.isDocumentRequirementValid && b.meta.isDocumentRequirementValid) {
          return 1;
        }

        return 0;
      });

      return {
        ...response.data,
        data: sortedData,
      };
    },
    getNextPageParam: (lastPage: HiringFeedsResponse) => lastPage.links.nextCursor ?? undefined,
    ...options,
  });
}

export type HiringFeedWithDetails = z.infer<typeof hiringFeedPlacementSchema.shape.attributes> & {
  id: string;
  meta: z.infer<typeof hiringFeedPlacementSchema.shape.meta>;
  workplace: WorkplaceInclude["attributes"] & { id: string };
  interviewSettings?: InterviewSettingsInclude["attributes"] & { id: string };
  workerRequirements?: Array<
    WorkerRequirementInclude["attributes"] & {
      id: string;
      requirement: RequirementInclude["attributes"] & { id: string };
    }
  >;
  credentialRequirements?: Array<
    WorkerRequirementInclude["attributes"] & {
      id: string;
      requirement: RequirementInclude["attributes"] & { id: string };
    }
  >;
  workerFavoritePlacementId?: string;
  tags?: TagInclude[];
};

export function transformResponseIntoHiringFeed(
  placement: HiringFeedPlacement,
  includedDataList: HiringFeedsResponse["included"],
  credentialRequirementConfiguration: Set<string>
): HiringFeedWithDetails {
  const workplace = includedDataList.find(
    (included): included is WorkplaceInclude =>
      included.type === API_TYPES.workplace &&
      included.id === placement.relationships.workplace.data.id
  );

  if (!workplace) {
    throw new Error("Workplace details not found for hiring feed placement");
  }

  let interviewSettings: (InterviewSettingsInclude["attributes"] & { id: string }) | undefined;
  if (placement.relationships.interviewSettings?.data) {
    const foundInterviewSettings = includedDataList.find(
      (included): included is InterviewSettingsInclude =>
        included.type === API_TYPES.interviewSettings &&
        included.id === placement.relationships.interviewSettings?.data.id
    );
    if (foundInterviewSettings) {
      interviewSettings = { ...foundInterviewSettings.attributes, id: foundInterviewSettings.id };
    }
  }

  // Extract worker requirements
  let workerRequirements: HiringFeedWithDetails["workerRequirements"];
  let credentialRequirements: HiringFeedWithDetails["credentialRequirements"];

  if (workplace.relationships.workerRequirements?.data) {
    const allWorkerRequirements = workplace.relationships.workerRequirements.data
      .map((workerRequirementRelationship) => {
        const workerRequirement = includedDataList.find(
          (included): included is WorkerRequirementInclude =>
            included.type === API_TYPES.workerRequirement &&
            included.id === workerRequirementRelationship.id &&
            included.attributes.qualifications.some((qualification) =>
              placement.attributes.workerTypes?.includes(qualification)
            )
        );

        if (!workerRequirement) {
          return undefined;
        }

        const requirement = includedDataList.find(
          (included): included is RequirementInclude =>
            included.type === API_TYPES.requirement &&
            included.id === workerRequirement.relationships.requirement.data.id
        );

        if (!requirement) {
          return undefined;
        }

        return {
          ...workerRequirement.attributes,
          id: workerRequirement.id,
          requirement: {
            ...requirement.attributes,
            id: requirement.id,
          },
        };
      })
      .filter((request): request is NonNullable<typeof request> => request !== undefined);

    credentialRequirements = allWorkerRequirements.filter((workerRequirement) =>
      credentialRequirementConfiguration.has(workerRequirement.requirement.id)
    );

    workerRequirements = allWorkerRequirements.filter(
      (workerRequirement) =>
        !credentialRequirementConfiguration.has(workerRequirement.requirement.id)
    );

    // eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing
    if (placement.meta.isExemptFromDocumentRequirements || workerRequirements.length === 0) {
      workerRequirements = undefined;
    }
  }

  const tags = placement.relationships.tags?.data
    .map((tagRef) => {
      const tagData = includedDataList.find(
        (included): included is TagInclude =>
          included.type === API_TYPES.tag && included.id === tagRef.id
      );
      if (!tagData) {
        return undefined;
      }

      return tagData;
    })
    .filter((tag): tag is TagInclude => tag !== undefined);

  return {
    ...placement.attributes,
    id: placement.id,
    meta: { ...placement.meta },
    workplace: { ...workplace.attributes, id: workplace.id },
    interviewSettings,
    workerRequirements,
    tags,
    workerFavoritePlacementId: placement.relationships.workerFavoritePlacement?.data?.id,
    credentialRequirements,
  };
}
